export interface TTSSession {
  sessionId: string;
  isPlaying: boolean;
}

// (client-generation types removed)

export class TTSService {
  private session: TTSSession | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private audioQueue: string[] = [];
  private isPlaying: boolean = false;
  private audioUnlocked: boolean = false;
  private audioContext: AudioContext | null = null;

  constructor() {}

  // (voice listing removed)

  // Enqueue an already-generated audio URL and play in-order
  enqueueUrl(url: string): void {
    try {
      const resolved = this.resolveUrl(url);
      this.audioQueue.push(resolved);
      if (!this.isPlaying) this.playNextInQueue();
    } catch (e) {
      console.warn("enqueueUrl failed", e);
    }
  }

  // Resolve relative vs absolute to a safe absolute URL (for CDN paths)
  private resolveUrl(u: string): string {
    try {
      return new URL(u, location.origin).toString();
    } catch {
      return u;
    }
  }

  // (client-side generation removed)

  // Play next audio in queue
  private async playNextInQueue(): Promise<void> {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const audioUrl = this.audioQueue.shift()!;

    // Play audio
    if (this.audioElement) {
      this.audioElement.src = audioUrl;
      try {
        // some mobile browsers (samsung) require an explicit load() before play
        this.audioElement.load();
      } catch {}
      this.audioElement.onended = () => {
        this.playNextInQueue();
      };
      this.audioElement.onerror = () => {
        console.warn("audio error, skipping", audioUrl);
        try {
          this.audioElement!.onerror = null as any;
        } catch {}
        this.playNextInQueue();
      };
      // fade in for smoother experience
      this.smoothPlay(this.audioElement).catch(async (error) => {
        console.error("❌ audio playback failed:", error);
        // fallback: try a direct play once (e.g., post-load on mobile)
        try {
          this.audioElement!.volume = 1;
          await this.audioElement!.play();
        } catch {}
        // continue queue even if this audio fails
        this.playNextInQueue();
      });
      if (this.session) this.session.isPlaying = true;
    } else {
      // Create temporary audio element if none set
      const tempAudio = new Audio(audioUrl);
      tempAudio.onended = () => {
        this.playNextInQueue();
      };
      tempAudio.onerror = () => {
        console.warn("temp audio error, skipping", audioUrl);
        this.playNextInQueue();
      };
      this.smoothPlay(tempAudio).catch((error) => {
        console.error("❌ temp audio playback failed:", error);
        // continue queue even if this audio fails
        this.playNextInQueue();
      });
    }

    console.log(`🎵 Playing audio (${this.audioQueue.length} queued)`);
  }

  // Smoothly start playback with a short fade-in
  private async smoothPlay(el: HTMLAudioElement): Promise<void> {
    try {
      el.volume = 0;
      await el.play();
      const start = performance.now();
      const DURATION = 300; // ms
      const step = (t: number) => {
        const p = Math.min(1, (t - start) / DURATION);
        el.volume = 0.1 + 0.9 * p; // avoid absolute silence edge cases
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    } catch (e) {
      // fallback to normal play if volume control blocked
      try {
        el.volume = 1;
        await el.play();
      } catch {}
    }
  }

  // Set audio element for playback
  setAudioElement(audioElement: HTMLAudioElement): void {
    this.audioElement = audioElement;
  }

  // On iOS/macOS Safari, audio must be unlocked by a user gesture
  async unlockAudio(): Promise<void> {
    if (this.audioUnlocked) return;
    try {
      // Try WebAudio resume
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      // Play a short silent audio to unlock HTMLMediaElement playback
      const silence = new Uint8Array([
        0x52, 0x49, 0x46, 0x46, 0x26, 0x00, 0x00, 0x00, 0x57, 0x41, 0x56, 0x45,
        0x66, 0x6d, 0x74, 0x20, 0x10, 0x00, 0x00, 0x00, 0x01, 0x00, 0x01, 0x00,
        0x22, 0x56, 0x00, 0x00, 0x44, 0xac, 0x00, 0x00, 0x02, 0x00, 0x10, 0x00,
        0x64, 0x61, 0x74, 0x61, 0x02, 0x00, 0x00, 0x00, 0x00, 0x00,
      ]);
      const blob = new Blob([silence.buffer], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      const el = this.audioElement ?? new Audio();
      el.src = url;
      el.muted = true;
      try {
        await el.play();
      } catch {}
      el.pause();
      el.muted = false;
      this.audioUnlocked = true;
      console.log("🔓 audio unlocked");
    } catch (e) {
      console.warn("failed to unlock audio", e);
    }
  }

  // Create a simple session
  createSession(sessionId: string): TTSSession {
    this.session = {
      sessionId,
      isPlaying: false,
    };
    return this.session;
  }

  // Get current session state
  getSession(): TTSSession | null {
    return this.session;
  }

  // (legacy completeWorkflow removed)

  // Cleanup
  cleanup(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = "";
    }
    this.audioQueue = [];
    this.isPlaying = false;
    this.session = null;
  }

  // Legacy method stubs for compatibility
  async publishSession(sessionId: string): Promise<TTSSession> {
    return this.createSession(sessionId);
  }

  async connectListener(sessionId: string): Promise<void> {
    // No-op for client-side approach
  }

  async subscribeToAudio(): Promise<void> {
    // No-op for client-side approach
  }

  async unpublishSession(): Promise<void> {
    this.cleanup();
  }

  async deleteSession(): Promise<void> {
    this.cleanup();
  }
}

// Export singleton instance
export const ttsService = new TTSService();
